import React, { useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { colors, spacing } from '../constants';
import { useUsersPageState } from '../hooks/pages/users/useUsersPageState';
import { useUsersData } from '../hooks/pages/users/useUsersData';
import { useUsersProjects } from '../hooks/pages/users/useUsersProjects';
import { useCreateUserForm } from '../hooks/pages/users/useCreateUserForm';
import { useUsersImportExport } from '../hooks/pages/users/useUsersImportExport';
import { ROLES } from '../constants/usersPage';
import ExportDialog from '../components/common/ExportDialog';
import ImportDialog from '../components/common/ImportDialog';
import PageHeader from '../components/usersPage/PageHeader';
import UserTable from '../components/usersPage/UserTable';
import CreateUserDialog from '../components/usersPage/CreateUserDialog';

const Users = () => {
  // Initialize all hooks
  const pageState = useUsersPageState();
  
  const { fetchUsers } = useUsersData({
    page: pageState.page,
    rowsPerPage: pageState.rowsPerPage,
    setUsers: pageState.setUsers,
    setTotalUsers: pageState.setTotalUsers,
    setLoading: pageState.setLoading,
  });

  const formHook = useCreateUserForm(fetchUsers, pageState.handleCloseDialog);
  
  const setFormProjects = useCallback(
    (projects) => formHook.setFormData((prev) => ({ ...prev, projects })),
    [formHook.setFormData]
  );

  const projectsHook = useUsersProjects({
    selectedRegion: formHook.formData.region,
    formProjects: formHook.formData.projects,
    setFormProjects,
    selectedRole: formHook.formData.role,
  });

  const importExportHook = useUsersImportExport(fetchUsers);

  // Fetch projects when dialog opens
  useEffect(() => {
    if (pageState.dialogOpen) {
      projectsHook.fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.dialogOpen]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!pageState.dialogOpen) {
      formHook.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.dialogOpen]);

  const handleCreateUserClick = () => {
    pageState.handleOpenDialog();
  };

  const handleExportClick = () => {
    pageState.handleOpenExportDialog();
  };

  const handleImportClick = () => {
    pageState.handleOpenImportDialog();
  };

  const handleExport = async (format) => {
    const success = await importExportHook.handleExport(format);
    if (success) {
      pageState.handleCloseExportDialog();
    }
  };

  const handleCloseExportDialog = () => {
    if (importExportHook.exporting) return;
    pageState.handleCloseExportDialog();
  };

  const handleCloseImportDialog = () => {
    if (importExportHook.importing) return;
    pageState.handleCloseImportDialog();
    importExportHook.setImportResults(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, p: { xs: spacing.sm, sm: spacing.md } }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <PageHeader
          onCreateUser={handleCreateUserClick}
          onImport={handleImportClick}
          onExport={handleExportClick}
        />

        <UserTable
          users={pageState.users}
          loading={pageState.loading}
          totalUsers={pageState.totalUsers}
          page={pageState.page}
          rowsPerPage={pageState.rowsPerPage}
          onChangePage={pageState.handleChangePage}
          onChangeRowsPerPage={pageState.handleChangeRowsPerPage}
        />

        {/* Create User Dialog */}
        <CreateUserDialog
          open={pageState.dialogOpen}
          onClose={pageState.handleCloseDialog}
          onCreate={() => formHook.handleCreateUser(projectsHook.projects)}
          creating={formHook.creating}
          formData={formHook.formData}
          errors={formHook.errors}
          whatsappError={formHook.whatsappError}
          projects={projectsHook.projects}
          loadingProjects={projectsHook.loadingProjects}
          availableRegions={formHook.availableRegions}
          roles={ROLES}
          onInputChange={formHook.handleInputChange}
          onWhatsAppChange={formHook.handleWhatsAppChange}
          onWhatsAppKeyPress={formHook.handleWhatsAppKeyPress}
          onRoleChange={formHook.handleRoleChange}
          onProjectsChange={formHook.handleProjectsChange}
        />

        {/* Export Dialog */}
        <ExportDialog
          open={pageState.exportDialogOpen}
          onClose={handleCloseExportDialog}
          onExport={handleExport}
          data={pageState.users}
          fileName="users"
          submitting={importExportHook.exporting}
          totalCount={pageState.totalUsers}
        />

        {/* Import Dialog */}
        <ImportDialog
          open={pageState.importDialogOpen}
          onClose={handleCloseImportDialog}
          onImport={importExportHook.handleImport}
          submitting={importExportHook.importing}
          importResults={importExportHook.importResults}
        />
      </Box>
    </Box>
  );
};

export default Users;
